import type { MouseEvent } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Icon, type IconName } from './Icon';

export interface BottomNavProps {
  /** The inbox's unread count; a badge on Mail when above zero. */
  unreadCount?: number | undefined;
  /** The inbox's folder id, so Mail knows when it is already *at* the inbox. */
  inboxId?: string | undefined;
}

interface Item {
  to: string;
  label: string;
  icon: IconName;
  /** True when a tap should scroll to the top instead of navigating. */
  isHome: (pathname: string, search: string) => boolean;
}

/**
 * The handheld bottom navigation bar: Mail · Calendar · Contacts · Tasks
 * (ADR 0048 §3.1, ADR 0069).
 *
 * Products only — Gmail's split. Folders are unbounded and live in the drawer;
 * a bottom bar holds a few fixed destinations. The shell renders it only on the
 * top-level list screens (`hasBottomNav` in app/screen.ts): inside a message,
 * a contact, compose or settings the space belongs to the content.
 *
 * Tapping the item you are already on scrolls the document to the top, the
 * Gmail and iOS convention — and it is the document, because on handheld the
 * document is the scroller. Tapping Mail from any other folder goes to the
 * inbox (a normal link to `/mail`).
 */
export function BottomNav({ unreadCount = 0, inboxId }: BottomNavProps) {
  const location = useLocation();

  const items: Item[] = [
    {
      to: '/mail',
      label: 'Mail',
      icon: 'inbox',
      isHome: (pathname, search) =>
        !new URLSearchParams(search).has('q') &&
        (pathname === '/mail' ||
          (inboxId !== undefined && pathname === `/mail/${encodeURIComponent(inboxId)}`)),
    },
    { to: '/calendar', label: 'Calendar', icon: 'calendar', isHome: (pathname) => pathname.startsWith('/calendar') },
    { to: '/contacts', label: 'Contacts', icon: 'contacts', isHome: (pathname) => pathname === '/contacts' },
    /*
     * Tasks (ADR 0069) is a product like the other three, so it earns a slot
     * rather than a drawer row: four items at 390 px are 97 px each, well past
     * the 48-px target, and a list you check off on the go should be one tap
     * from anywhere. Home is any list, not only the first.
     */
    {
      to: '/tasks',
      label: 'Tasks',
      icon: 'tasks',
      isHome: (pathname) => pathname === '/tasks' || /^\/tasks\/[^/]+$/.test(pathname),
    },
  ];

  const onClick = (item: Item) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (!item.isHome(location.pathname, location.search)) return;
    event.preventDefault();
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  };

  return (
    <nav className="bottom-nav" aria-label="Products">
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} className="bottom-nav__item" onClick={onClick(item)}>
          <span className="bottom-nav__icon">
            <Icon name={item.icon} size={24} />
            {item.to === '/mail' && unreadCount > 0 && (
              <span className="bottom-nav__badge" aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </span>
          <span className="bottom-nav__label">{item.label}</span>
          {item.to === '/mail' && unreadCount > 0 && (
            <span className="visually-hidden">, {unreadCount} unread</span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
